export interface INotificationLike {
  id: number
  type: string
  severity: string
  title: string
  message: string
  adapter?: string | null
  metadata?: string | null
  read_at?: string | null
  created_at: string
}

export interface INotificationGroup {
  key: string
  type: string
  severity: string
  title: string
  message: string
  count: number
  unreadCount: number
  unreadIds: number[]
  /** ISO timestamp of the most recent occurrence in the group. */
  latest: string
  /** Newest first. */
  items: INotificationLike[]
}

export type TNotificationSectionId = "today" | "earlier-this-week" | "older"

export interface INotificationSection {
  id: TNotificationSectionId
  groups: INotificationGroup[]
}

const MEETING_TYPES = new Set(["meeting.processed", "meeting.failed"])
const SECTION_ORDER: readonly TNotificationSectionId[] = ["today", "earlier-this-week", "older"]

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value)
}

export function readNotificationMetadata(raw: string | null | undefined): Record<string, unknown> | null {
  if (!raw) return null
  try {
    const parsed: unknown = JSON.parse(raw)
    return isRecord(parsed) ? parsed : null
  } catch {
    return null
  }
}

/** Meeting id stored on the row, when the notification is about one meeting. */
export function meetingIdOf(notification: Pick<INotificationLike, "metadata">): string | null {
  const id = readNotificationMetadata(notification.metadata)?.meetingId
  return typeof id === "string" && id.length > 0 ? id : null
}

/**
 * Meeting rows group per meeting. Budget and model rows still group by
 * type + adapter, which is how those alerts repeat.
 */
export function notificationGroupKey(notification: INotificationLike): string {
  if (MEETING_TYPES.has(notification.type)) {
    const meetingId = meetingIdOf(notification)
    if (meetingId) return `${notification.type}::meeting::${meetingId}`
  }
  return `${notification.type}::${notification.adapter ?? ""}`
}

function byNewest(a: INotificationLike, b: INotificationLike): number {
  if (a.created_at === b.created_at) return b.id - a.id
  return a.created_at < b.created_at ? 1 : -1
}

/**
 * Group the bell. Meeting notifications never collapse across meetings.
 * Within a group, `items[0]` is the newest row — that is the one "Open" uses.
 */
export function dedupeNotifications(items: INotificationLike[]): INotificationGroup[] {
  const groups = new Map<string, INotificationGroup>()
  for (const notification of [...items].sort(byNewest)) {
    const key = notificationGroupKey(notification)
    const existing = groups.get(key)
    if (existing) {
      existing.count += 1
      existing.items.push(notification)
      if (!notification.read_at) {
        existing.unreadCount += 1
        existing.unreadIds.push(notification.id)
      }
      continue
    }
    groups.set(key, {
      key,
      type: notification.type,
      severity: notification.severity,
      title: notification.title,
      message: notification.message,
      count: 1,
      unreadCount: notification.read_at ? 0 : 1,
      unreadIds: notification.read_at ? [] : [notification.id],
      latest: notification.created_at,
      items: [notification],
    })
  }
  return [...groups.values()].sort((a, b) => (a.latest < b.latest ? 1 : a.latest > b.latest ? -1 : 0))
}

/** Monday-start local week. Today is its own section. */
export function notificationSection(iso: string, now: Date): TNotificationSectionId {
  const when = new Date(iso)
  if (Number.isNaN(when.getTime())) return "older"
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  if (when.getTime() >= startOfToday.getTime()) return "today"
  const weekday = startOfToday.getDay()
  const daysSinceMonday = weekday === 0 ? 6 : weekday - 1
  const startOfWeek = new Date(startOfToday)
  startOfWeek.setDate(startOfWeek.getDate() - daysSinceMonday)
  if (when.getTime() >= startOfWeek.getTime()) return "earlier-this-week"
  return "older"
}

/** Day sections, newest first inside each. Empty sections are omitted. */
export function sectionNotifications(items: INotificationLike[], now: Date): INotificationSection[] {
  const buckets = new Map<TNotificationSectionId, INotificationGroup[]>()
  for (const group of dedupeNotifications(items)) {
    const id = notificationSection(group.latest, now)
    const list = buckets.get(id) ?? []
    list.push(group)
    buckets.set(id, list)
  }
  const sections: INotificationSection[] = []
  for (const id of SECTION_ORDER) {
    const groups = buckets.get(id)
    if (groups && groups.length > 0) sections.push({ id, groups })
  }
  return sections
}

/** Pathname of `/meetings/$id` and its child routes. Null on the list and elsewhere. */
export function meetingIdFromPath(pathname: string): string | null {
  const match = /^\/meetings\/([^/]+)/.exec(pathname)
  const id = match?.[1]
  return id && id.length > 0 ? id : null
}

/** Unread "ready" rows for the meeting the user is already looking at. */
export function unreadReadyIdsForMeeting(items: INotificationLike[], meetingId: string): number[] {
  const ids: number[] = []
  for (const notification of items) {
    if (notification.read_at) continue
    if (notification.type !== "meeting.processed") continue
    if (meetingIdOf(notification) !== meetingId) continue
    ids.push(notification.id)
  }
  return ids
}
